// src/lib/conversation-vocale.js — LE MODE CONVERSATION.
//
// Dimitri, 2026-09-09 : « Je ne veux pas qu'il pré-remplisse mes questions. Je
// veux qu'on discute. Je parle, il me répond à voix haute, je le coupe au
// milieu, je pose une autre question, il reprend. Comme une conversation avec
// quelqu'un. »
//
// ══ CE QUE CE MODULE EST ═══════════════════════════════════════════════════
//
// Une MACHINE À ÉTATS, sans React et sans DOM au-delà des deux API du
// navigateur. Elle vit hors du composant pour une raison simple : elle survit
// aux rendus, et elle se teste sans écran.
//
//   ferme → ecoute ⇄ envoi → parle → ecoute …
//
// ══ L'ÉCHO — CE QUI A ÉTÉ MESURÉ, ET CE QU'ON EN FAIT ══════════════════════
//
// Dimitri, 2026-09-09 : « je n'utilise pas l'application avec un casque. Le
// problème doit être résolu, pas contourné. Regarde l'annulation d'écho du
// navigateur. » Il a raison sur le principe, et voici la mesure.
//
//   getUserMedia({echoCancellation, noiseSuppression, autoGainControl})
//   → getSettings() rend les TROIS à `true` : ils sont APPLIQUÉS, pas
//     seulement demandés.
//
//   SpeechRecognition.prototype = start, stop, abort, lang, continuous,
//   interimResults, maxAlternatives, processLocally, phrases + événements.
//   AUCUNE entrée pour un flux.
//
// ⚠️ ON NE PEUT DONC PAS DONNER L'AUDIO NETTOYÉ À LA RECONNAISSANCE. Ce n'est
// pas une limite de notre code : c'est la surface de l'API, vérifiée dans un
// navigateur réel, pas déduite.
//
// ══ LA SOLUTION : SÉPARER COMPRENDRE ET DÉTECTER ═══════════════════════════
//
// Pour couper la parole, il n'est pas nécessaire de COMPRENDRE ce qui est dit.
// Il suffit de savoir que QUELQU'UN PARLE. Et ça, on peut le mesurer sur le
// flux nettoyé, où le haut-parleur EST annulé.
//
//   Il parle  → reconnaissance FERMÉE (aucun écho possible, même au pire)
//              + surveillance d'énergie sur le flux AEC → la coupure marche.
//   Il écoute → reconnaissance OUVERTE, surveillance arrêtée.
//
// Le micro reste ouvert en permanence (le flux propre), donc l'annulation
// d'écho reste active sur le périphérique — ce dont la reconnaissance peut
// hériter, sans que ce soit garanti par la spécification.
//
// ⚠️ ET SI ÇA NE SUFFIT PAS SUR UNE MACHINE DONNÉE : on compte les échos qui
// passent malgré tout, et au-delà de trois on cesse d'écouter pendant qu'il
// parle — en le disant. C'est le repli que Dimitri propose lui-même, appliqué
// SUR PREUVE et jamais par précaution.
//
// ══ LES TROIS AUTRES PIÈGES ════════════════════════════════════════════════
//
// · CHROME COUPE L'ÉCOUTE TOUT SEUL, même en `continuous`. Sans relance, le
//   mode reste ouvert À L'ÉCRAN et devient SOURD : le pire des états, parce
//   qu'il a l'air de marcher. On relance, borné — trois échecs et on ferme.
// · UNE QUESTION QUI PART TOUTE SEULE PART VRAIMENT. Plus de clic pour
//   rattraper : plancher de mots + délai de silence.
// · CE QUI AGIT NE PART JAMAIS À LA VOIX (réserve de Dimitri). Ce module
//   n'exécute RIEN : il envoie des QUESTIONS. Et l'écran refuse tout
//   déplacement automatique tant que le mode est ouvert.

import { estDisponible, ecouter } from "./dictee";
import { dire, taire, voixDisponible, coupeLisible } from "./voix";
import { ouvrirMicroPropre, echoAnnule } from "./micro-propre";
import { lireAcquiescement } from "./operateur-acquiescement";
import { PAS_ENCORE_LU, refusTropTot } from "./carte-dite";

// Moins de trois mots, c'est un bruit, un « oui » à quelqu'un d'autre, ou une
// hésitation. On n'envoie pas.
const ASSEZ_DE_MOTS = 3;
// Le temps de silence après lequel on considère la phrase finie. Assez long
// pour laisser respirer au milieu d'une question, assez court pour ne pas
// donner l'impression qu'il n'écoute plus.
const SILENCE_MS = 1100;
// De quoi accuser réception, tout de suite, à voix haute. Courts : ils doivent
// être finis avant que la vraie réponse n'arrive.
// ⚠️ On n'invente pas « je réfléchis » — ce serait un mensonge poli, et
// toujours le même. On accuse réception, ce qui est vrai.
const _ACCUSES = ["Je regarde.", "Une seconde.", "Je vérifie."];
// Au-delà, on cesse de relancer : quelque chose ne va pas et s'acharner
// donnerait un mode ouvert et sourd.
const RELANCES_MAX = 3;
const ECHOS_AVANT_ALERTE = 3;
// Au-delà, on cesse d'attendre que la carte ait lu sa valeur actuelle : on le
// DIT, et rien n'est posé comme lu. Quatre secondes de bouche fermée après la
// prose est déjà long ; dans la quasi-totalité des cas l'aperçu est arrivé
// pendant qu'il parlait (il part au montage de la carte, donc au rendu de la
// réponse, bien avant la fin de la prose).
const ATTENTE_APERCU_MS = 4000;
const PAS_DE_SCRUTATION_MS = 250;
// ══ LA RÉMANENCE DE L'ÉCHO ════════════════════════════════════════════════
// ⚠️ L'OREILLE ROUVRE AVANT QUE LE HAUT-PARLEUR NE SE TAISE VRAIMENT. La fin
// de parole est ESTIMÉE (ou rendue par `onend`, qui part parfois un souffle
// trop tôt) ; la queue de la phrase et la latence du moteur de reconnaissance
// font arriver des fragments APRÈS la réouverture. `enTrainDeDire` étant vidé
// à ce moment-là, `ressembleAuxMotsDits` n'avait plus AUCUNE référence
// (`d.size === 0` ⇒ `false`) : le fragment passait pour une vraie phrase. Un
// mot suffisait — et depuis que le plancher laisse passer un mot sur une carte
// prête, ce mot pouvait être un assentiment. On garde donc ce qu'on vient de
// dire encore une seconde et demie après avoir rendu la parole.
const REMANENCE_ECHO_MS = 1500;

/** Normalise pour comparer : sans accents, sans ponctuation, en minuscules. */
export function normaliser(t) {
  return String(t || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Ce qu'on entend est-il l'écho de ce qu'on dit ?
 *
 * ⚠️ On ne cherche PAS l'égalité : la reconnaissance déforme, et elle n'entend
 * qu'un fragment de ce qui est prononcé. On mesure la part des mots entendus
 * qui figurent dans ce qu'on est en train de dire. Au-delà de 60 %, c'est
 * l'écho — un humain qui pose une VRAIE question n'emploie pas huit mots sur
 * dix de la phrase qu'il interrompt.
 */
export function ressembleAuxMotsDits(entendu, enTrainDeDire) {
  const e = normaliser(entendu).split(" ").filter(Boolean);
  const d = new Set(normaliser(enTrainDeDire).split(" ").filter(Boolean));
  if (!e.length || !d.size) return false;
  const communs = e.filter((m) => d.has(m)).length;
  return communs / e.length >= 0.6;
}

/** Assez pour être une question ? */
export function assezPourEnvoyer(t) {
  return normaliser(t).split(" ").filter(Boolean).length >= ASSEZ_DE_MOTS;
}

/**
 * Ouvre le mode conversation.
 *
 * @param {object} h
 *   - poser(question) → Promise<{texte:string}> : envoie et rend la réponse.
 *   - onEtat(etat)    : 'ecoute' | 'envoi' | 'parle' | 'ferme'
 *   - onEntendu(texte): ce qui est compris, en direct (peut être "")
 *   - onAvis(message) : ce qu'il faut DIRE à l'écran (écho, panne, fermeture)
 * @returns {{fermer:Function, enCours:Function}}
 */
export function ouvrirConversation(h = {}) {
  const dit = (fn, ...a) => {
    try {
      if (typeof h[fn] === "function") h[fn](...a);
    } catch (e) {
      // Un écran qui plante ne doit pas fermer le micro.
      console.error("[vocal]", fn, e?.message || e);
    }
  };

  if (!estDisponible()) {
    dit(
      "onAvis",
      "Votre navigateur ne sait pas écouter (Firefox, notamment). La conversation vocale n'est pas possible ici.",
    );
    dit("onEtat", "ferme");
    return { fermer() {}, enCours: () => false };
  }

  let ferme = false;
  // ⚠️ `null` ET NON "ecoute" : `poser` ne prévient que sur CHANGEMENT. Parti
  // de "ecoute", le premier état n'aurait jamais été annoncé — l'écran serait
  // resté sans savoir que le micro est ouvert. Trouvé par un test qui
  // attendait autre chose que le code ; c'est le code qui avait tort.
  let etat = null;
  let prise = null;
  let minuteur = null;
  let enAttente = ""; // ce qui est entendu et pas encore envoyé
  let enTrainDeDire = ""; // ce qu'il prononce EN CE MOMENT (pour l'écho)
  let relances = 0;
  let echos = 0;
  // Le micro NETTOYÉ : il reste ouvert toute la conversation. Il sert à deux
  // choses — mettre le périphérique en mode annulation d'écho, et détecter la
  // parole pendant qu'il parle (la reconnaissance étant fermée à ce
  // moment-là).
  let micro = null;
  let arreterSurveillance = null;
  // Passe à vrai si l'annulation ne suffit pas sur cette machine : on cesse
  // alors d'écouter pendant qu'il parle. SUR PREUVE, jamais par précaution.
  let demiDuplex = false;
  // Dit une seule fois : « je n'arrive pas à parler ». Le répéter à chaque
  // réponse noierait le fil.
  let muetSignale = false;
  // On alterne : entendre trois fois « une seconde » de suite sonne faux.
  let accuse = 0;

  // ══ CE QU'IL A LU, ET QUAND (B2, 2026-09-22) ═════════════════════════════
  //
  // ⚠️ DANS LA FERMETURE, PAS DANS REACT. Le verdict du « oui » se rend en
  // même temps que la phrase est entendue ; une valeur d'état React lue au
  // rendu suivant arriverait après. Même motif que `enVocalRef` côté écran.
  //
  // ⚠️ UNE EMPREINTE, PAS UN BOOLÉEN. Une carte d'ÉVÉNEMENT peut apparaître
  // entre la lecture et le « oui » sans repasser par `envoyer()` : un drapeau
  // « j'ai lu quelque chose » lui ferait hériter du « lu » d'une autre carte.
  // L'identité répond à la seule question qui compte : ai-je lu CELLE-CI ?
  let carteDiteEmpreinte = null;
  let carteDiteA = 0;
  // 'fait' | 'reste' | null — un « oui » pendant le FAIT est trop tôt ; pendant
  // le RESTE, le fait a été entendu et le « oui » se traite normalement.
  let phaseCarte = null;
  // Périme les rappels d'une séquence abandonnée : une fin de phrase qui
  // revient après une coupure ne doit rien enchaîner.
  let sequence = 0;
  // ⚠️ LE FAIT **NU**, sans préfixe. Le refus « je n'avais pas fini » se
  // recompose à chaque « oui » prématuré : préfixer un fait DÉJÀ préfixé
  // donnait « Je n'avais pas fini. Je n'avais pas fini. … », non borné jusqu'à
  // la coupe des sept cents caractères (une trentaine de répétitions).
  let faitEnCours = "";
  // ⚠️ TOUT CE QU'ON A DIT DANS CE TOUR — prose, fait, reste — et pas seulement
  // le dernier énoncé. La queue qui revient dans le micro peut être celle de
  // n'importe lequel des trois : garder le seul dernier laissait passer l'écho
  // du FAIT (le plus long, donc le plus susceptible de traîner) juste après la
  // fin du reste.
  let ditDansCeTour = "";
  let dernierDit = "";
  let minuteurEcho = null;

  const poser = (e) => {
    if (etat === e) return;
    etat = e;
    dit("onEtat", e);
  };

  // ══ DIRE UNE PHRASE, ET SAVOIR QUAND ELLE EST FINIE ══════════════════════
  //
  // ⚠️ DEUX SOURCES, ET LA PREMIÈRE GAGNE — jamais les deux. `onend` est la
  // vérité quand il part ; le minuteur estimé reste en PLAFOND parce qu'il ne
  // part pas sur tous les navigateurs, et qu'un mode ouvert et SOURD est le
  // pire des états (il a l'air de marcher). Sans le garde-fou de libération
  // unique, les deux s'exécuteraient et la suite se jouerait deux fois.
  //
  // ⚠️ ET LE PLAFOND SE CALCULE SUR LA CHAÎNE FINALE, celle qui sort vraiment
  // du haut-parleur (normalisée puis coupée) — pas sur le brut. Un brut de
  // deux mille caractères réduit à sept cents donnerait une attente trois fois
  // trop longue, pendant laquelle rien n'écoute.
  function direEtAttendre(texte, apres) {
    const moi = ++sequence;
    let libere = false;
    const liberer = () => {
      if (libere || ferme || moi !== sequence) return;
      libere = true;
      clearTimeout(minuteur);
      minuteur = null;
      apres();
    };
    // ⚠️ POSÉ AVANT DE DIRE : c'est la seule référence qui permette de
    // reconnaître l'écho pendant que ça sort du haut-parleur. Et il vaut
    // l'énoncé EN COURS, la carte comprise — sinon les mots de la carte ne
    // « ressembleraient » à rien et elle se couperait elle-même.
    enTrainDeDire = texte;
    ditDansCeTour = ditDansCeTour ? `${ditDansCeTour} ${texte}` : texte;
    // ⚠️ LES SORTIES SYNCHRONES DE `dire()` NE LIBÈRENT PAS ICI. `voix.js`
    // appelle `onFini` AVANT de rendre `false` (navigateur muet, texte vide,
    // synthèse qui lève) : enchaîner `apres()` puis rendre `false` faisait
    // jouer la suite DEUX fois — et, dans `direLeFait`, posait l'empreinte
    // « il l'a entendue » sur un fait JAMAIS PRONONCÉ. C'est l'appelant qui
    // décide de ces cas-là, et lui seul.
    let rendu = false;
    const parle = dire(texte, {
      impose: true,
      onFini: () => {
        if (!rendu) return;
        liberer();
      },
    });
    rendu = true;
    if (!parle) {
      enTrainDeDire = "";
      return false;
    }
    const dit = coupeLisible(texte).texte;
    minuteur = setTimeout(
      liberer,
      Math.min(Math.max(dit.length * 70, 1500), 45000),
    );
    return true;
  }

  function arreterEcoute() {
    try {
      if (prise) prise.arreter();
    } catch {
      /* déjà arrêtée */
    }
    prise = null;
  }

  function fermer(avis) {
    if (ferme) return;
    ferme = true;
    clearTimeout(minuteur);
    clearTimeout(minuteurEcho);
    dernierDit = "";
    taire();
    if (arreterSurveillance) arreterSurveillance();
    arreterSurveillance = null;
    if (micro) micro.fermer();
    micro = null;
    arreterEcoute();
    if (avis) dit("onAvis", avis);
    poser("ferme");
  }

  async function envoyer() {
    const q = enAttente.trim();
    enAttente = "";
    dit("onEntendu", "");
    if (!q || ferme) return;
    // ⚠️ CE QU'IL AVAIT LU VOYAGE AVEC LA PHRASE, PUIS S'EFFACE. Il est lu au
    // moment où la phrase a été prononcée, pas au moment où le verdict se
    // rend (une seconde plus tard) : une carte lue vaut pour le « oui » qui la
    // suit, jamais pour celui d'après.
    const lu = { carteDiteEmpreinte, carteDiteA };
    oublierLaLecture();
    ditDansCeTour = "";
    sequence += 1;
    clearTimeout(minuteur);
    poser("envoi");
    // ── ZÉRO SILENCE À LA VOIX ─────────────────────────────────────────────
    // ⚠️ Dimitri, 2026-09-09 : « le silence dans une conversation parlée est
    // bien pire qu'à l'écran ». Cinq secondes de rien, au téléphone, se lisent
    // comme « il ne m'a pas entendu » — et on répète sa question, ce qui
    // relance tout.
    //
    // ⚠️ ON NE DIT PAS « je réfléchis » : ce serait un mensonge poli et
    // toujours le même. On accuse RÉCEPTION, ce qui est vrai, et c'est court
    // pour ne pas manger le début de la vraie réponse.
    //
    // Ce petit accusé n'est PAS enregistré comme « ce qu'on est en train de
    // dire » (enTrainDeDire) : il dure moins d'une seconde, et le compter
    // ferait passer une vraie question posée juste après pour un écho.
    dire(_ACCUSES[accuse++ % _ACCUSES.length], { impose: true });
    let reponse = null;
    try {
      reponse = await h.poser(q, lu);
    } catch (e) {
      console.error("[vocal] envoi", e?.message || e);
    }
    if (ferme) return;
    const texte = (reponse && reponse.texte) || "";
    if (!texte) {
      poser("ecoute");
      return;
    }
    poser("parle");
    // ⚠️ `impose` : LE MODE EST LE CONSENTEMENT. On a appuyé sur « parler » —
    // exiger un second interrupteur, c'est demander deux fois la même
    // permission. C'est ce qui a rendu l'opérateur MUET le 2026-09-09 : le
    // second interrupteur avait disparu de l'en-tête, personne ne pouvait
    // l'allumer, et le silence était parfaitement plausible.
    //
    // ⚠️ PROSE PUIS CARTE : UNE SEULE SÉQUENCE D'ÉTAT (B2, MAJEUR 4). La fin
    // de la prose ne rouvre l'oreille QUE s'il n'y a rien à dire ensuite.
    // Rouvrir entre les deux ferait écouter la carte par le micro (elle
    // s'entendrait elle-même, et `etat === 'ecoute'` défait la garde d'état) ;
    // fermer l'oreille « le temps de voir » la laisserait fermée pendant qu'il
    // parle. La surveillance d'énergie, elle, couvre TOUTE la séquence.
    armerLaCoupure();
    const parle = direEtAttendre(texte, () => puisLaCarte(texte));
    if (!parle) {
      // Rien ne sort (navigateur muet) : on ne fait pas semblant d'attendre la
      // fin d'une phrase qui n'est jamais prononcée. ET ON LE DIT — un mode
      // « conversation » qui n'a jamais de voix doit s'annoncer.
      relacher();
      if (!muetSignale) {
        muetSignale = true;
        dit(
          "onAvis",
          "Je n'arrive pas à parler sur ce navigateur — mes réponses restent à l'écran.",
        );
      }
      return;
    }
  }

  // ── PENDANT QU'IL PARLE ──────────────────────────────────────────────────
  // ⚠️ LA RECONNAISSANCE EST FERMÉE. C'est ce qui rend l'écho IMPOSSIBLE,
  // même si l'annulation du navigateur ne couvre pas la synthèse sur cette
  // machine. On ne pouvait pas lui donner le flux nettoyé (l'API ne l'accepte
  // pas, mesuré) : on la met donc hors circuit.
  // ⚠️ ET LA COUPURE PASSE PAR LE FLUX NETTOYÉ. Pour couper la parole, il
  // suffit de savoir que quelqu'un PARLE — pas de comprendre quoi. L'énergie
  // du flux AEC le dit, et le haut-parleur y est annulé. C'est ça, la réponse
  // au « je n'ai pas de casque ».
  function armerLaCoupure() {
    arreterEcoute();
    if (arreterSurveillance) arreterSurveillance();
    arreterSurveillance = null;
    if (micro && !demiDuplex) {
      arreterSurveillance = micro.surveiller(() => {
        if (ferme || etat !== "parle") return;
        // Il parle par-dessus : ce qu'il n'a pas fini d'entendre n'a pas été
        // entendu.
        sequence += 1;
        clearTimeout(minuteur);
        taire();
        oublierLaLecture();
        relacher();
      });
    }
  }

  /** Il a fini de parler : on rouvre l'oreille, et on range la séquence. */
  function relacher() {
    sequence += 1;
    clearTimeout(minuteur);
    minuteur = null;
    if (arreterSurveillance) arreterSurveillance();
    arreterSurveillance = null;
    // ⚠️ CE QU'ON VIENT DE DIRE SURVIT À LA RÉOUVERTURE (voir REMANENCE_ECHO_MS).
    if (ditDansCeTour) {
      dernierDit = ditDansCeTour;
      ditDansCeTour = "";
      clearTimeout(minuteurEcho);
      minuteurEcho = setTimeout(() => {
        dernierDit = "";
      }, REMANENCE_ECHO_MS);
    }
    enTrainDeDire = "";
    phaseCarte = null;
    faitEnCours = "";
    poser("ecoute");
    demarrerEcoute();
  }

  /** Ce qu'il a lu ne vaut plus : personne ne pourra s'appuyer dessus. */
  function oublierLaLecture() {
    carteDiteEmpreinte = null;
    carteDiteA = 0;
    phaseCarte = null;
    faitEnCours = "";
  }

  /** Le guichet des cartes, tel que l'écran le rend. Jamais d'exception. */
  function guichet(prose) {
    try {
      if (typeof h.carteADire !== "function") return { etat: "absente" };
      return h.carteADire(prose) || { etat: "absente" };
    } catch (e) {
      console.error("[vocal] carte", e?.message || e);
      return { etat: "absente" };
    }
  }

  // ══ LA CARTE, APRÈS LA PROSE ═════════════════════════════════════════════
  //
  // ⚠️ IL N'Y A PAS D'ÉNONCÉ 0, ET C'EST MESURÉ (2026-09-22). L'avertissement
  // « la nuit du samedi 26 est déjà vendue » est posé EN TÊTE de
  // `reponse.texte` par le serveur (`lib/operateur-nuit-vendue.js`) : il est
  // donc dit par la PROSE, en entier, avant que `puisLaCarte` ne soit appelée
  // — `puisLaCarte` n'est rien d'autre que le `onFini` de la prose. Et la
  // coupe des sept cents caractères garde la TÊTE (`coupeLisible`) : une
  // phrase placée devant survit à toutes les proses. Un énoncé 0 la dirait une
  // seconde fois. Ce qui reste, et qui est fait ailleurs : rappeler le GENRE
  // en tête de l'énoncé 1 (`avecMarqueur`, `src/lib/carte-dite.js`).
  //
  // ⚠️ ON NE LIT JAMAIS UNE CARTE QUE « OUI » NE LANCERAIT PAS. Le guichet part
  // de la liste des cartes PRÊTES, jamais de la dernière carte montée : une
  // carte de réponse périmée reste à l'écran (avec son bandeau « oui ne la
  // lance plus ») pendant qu'une carte d'événement, elle, est lançable. Lire
  // l'une et lancer l'autre serait le même défaut, par un autre chemin.
  function puisLaCarte(prose, depuis) {
    if (ferme) return;
    const c = guichet(prose);
    if (c.etat === "attente") {
      // L'aperçu n'est pas encore lu : la carte dirait « de rien à quarante
      // euros ». On patiente, borné — et au bout, on le DIT.
      const debut = depuis || Date.now();
      if (Date.now() - debut < ATTENTE_APERCU_MS) {
        clearTimeout(minuteur);
        minuteur = setTimeout(
          () => puisLaCarte(prose, debut),
          PAS_DE_SCRUTATION_MS,
        );
        return;
      }
      if (!direEtAttendre(PAS_ENCORE_LU, relacher)) relacher();
      return;
    }
    if (c.etat === "plusieurs") {
      // Deux cartes : il les NOMME et il refuse. Deviner lancerait celle qu'il
      // ne regardait pas.
      if (!c.texte || !direEtAttendre(c.texte, relacher)) relacher();
      return;
    }
    if (c.etat !== "prete" || !c.fait) {
      relacher();
      return;
    }
    direLeFait(c);
  }

  /**
   * ÉNONCÉ 1 — LE FAIT. C'est lui, et lui seul, qui pose « il l'a entendue ».
   *
   * ⚠️ L'EMPREINTE SE POSE À LA FIN, JAMAIS À L'OUVERTURE. Posée au début,
   * elle vaudrait « entendue » sur une phrase coupée à sa deuxième seconde —
   * et la fenêtre de vingt secondes commencerait à compter avant la phrase.
   */
  function direLeFait(c) {
    phaseCarte = "fait";
    faitEnCours = c.faitNu || c.fait;
    relectureDe = { pose: c.pose, empreinte: c.empreinte };
    const fini = () => {
      // `pose: false` — il a parlé (jamais de silence) mais il n'a pas pu
      // nommer le logement d'un geste d'argent : rien ne s'appuiera dessus.
      if (c.pose !== false && c.empreinte) {
        carteDiteEmpreinte = c.empreinte;
        carteDiteA = Date.now();
      }
      phaseCarte = "reste";
      if (!c.reste || !direEtAttendre(c.reste, relacher)) relacher();
    };
    if (!direEtAttendre(c.fait, fini)) relacher();
  }

  // Ce qu'on reposera si la relecture du fait va au bout.
  let relectureDe = { pose: true, empreinte: null };

  /**
   * Une phrase courte vaut-elle l'envoi ?
   *
   * ⚠️ SEULEMENT LES FORMES FERMÉES de `LANCE` / `ANNULE`, et seulement quand
   * une carte est PRÊTE. Toute autre phrase courte reste jetée : « euh »,
   * un bruit, un mot adressé à quelqu'un d'autre.
   */
  function assentimentSurCartePrete(t) {
    const { verdict } = lireAcquiescement(t);
    if (verdict !== "lance" && verdict !== "annule") return false;
    const c = guichet("");
    return c.etat === "prete" || c.etat === "plusieurs" || c.etat === "attente";
  }

  function entendu(texte, definitif) {
    if (ferme) return;

    // ── L'ÉCHO ───────────────────────────────────────────────────────────
    if (etat === "parle" && ressembleAuxMotsDits(texte, enTrainDeDire)) {
      echos += 1;
      if (echos >= ECHOS_AVANT_ALERTE && !demiDuplex) {
        // ⚠️ L'annulation d'écho n'a pas suffi sur cette machine. On cesse
        // d'écouter pendant qu'il parle — le repli de Dimitri, appliqué sur la
        // MESURE et non par précaution. On perd la coupure au milieu ; on
        // gagne un mode qui marche sans casque.
        demiDuplex = true;
        arreterEcoute();
        dit(
          "onAvis",
          "Je m'entendais parler dans le micro. Je n'écoute plus pendant que je réponds — attendez la fin de ma phrase pour reprendre.",
        );
      }
      return;
    }

    // ── UN « OUI » PENDANT LE FAIT : TROP TÔT, ET IL LE DIT ──────────────
    //
    // ⚠️ LE DÉFAUT QU'IL RENCONTRERA À LA PREMIÈRE UTILISATION RÉELLE. Une
    // carte de prix fait treize secondes ; un « oui » à la neuvième tombait
    // dans la branche « coupure », la carte s'arrêtait net, et le « oui » — un
    // seul mot — était jeté par le plancher SANS UN MOT. L'écran ne montrait
    // rien : à la voix, il n'avait que le silence pour comprendre.
    //
    // Ici il coupe, il DIT qu'il n'avait pas fini, et il relit le seul fait.
    // Jamais de silence, dans aucun cas.
    if (
      etat === "parle" &&
      phaseCarte === "fait" &&
      definitif &&
      lireAcquiescement(texte).verdict === "lance"
    ) {
      sequence += 1;
      clearTimeout(minuteur);
      taire();
      enAttente = "";
      dit("onEntendu", "");
      // ⚠️ LE FAIT **NU** : préfixer un fait déjà préfixé donnait « Je n'avais
      // pas fini. Je n'avais pas fini. … » à chaque « oui » prématuré.
      const fait = faitEnCours;
      // La relecture repose « lu » si elle va au bout : c'est bien le fait
      // qu'il aura entendu en entier, cette fois.
      direLeFait({
        fait: refusTropTot(fait),
        faitNu: fait,
        reste: "",
        pose: relectureDe.pose,
        empreinte: relectureDe.empreinte,
      });
      return;
    }

    // ── L'ÉCHO QUI ARRIVE APRÈS LA RÉOUVERTURE ───────────────────────────
    // ⚠️ CE N'EST PAS UN DOUBLON DU BLOC AU-DESSUS. Là-haut on est en train de
    // parler ; ici on a RENDU la parole, et c'est la queue de la phrase qui
    // revient. Sans ce bloc, un fragment d'une seconde et demie plus tard
    // passait le plancher et pouvait lancer un geste : l'opérateur se lançait
    // lui-même.
    if (
      etat === "ecoute" &&
      dernierDit &&
      ressembleAuxMotsDits(texte, dernierDit)
    ) {
      echos += 1;
      return;
    }

    // ── LA COUPURE ───────────────────────────────────────────────────────
    // Il parle, on l'interrompt : il se tait et écoute. C'est la demande.
    if (etat === "parle") {
      sequence += 1;
      clearTimeout(minuteur);
      taire();
      // ⚠️ CE QU'IL N'A PAS FINI D'ENTENDRE N'A PAS ÉTÉ ENTENDU. Une seule
      // exception, et elle est étroite : un ASSENTIMENT pendant l'énoncé 2. Là
      // le fait EST allé au bout — c'est tout l'intérêt de l'avoir séparé — et
      // couper les avertissements pour dire « oui » ne défait pas ce qu'il a
      // entendu.
      //
      // ⚠️ MAIS TOUTE AUTRE PHRASE L'EFFACE, énoncé 2 compris : s'il part sur
      // autre chose, le « oui » d'après ne porte plus sur cette carte-là. Sans
      // ça, une lecture d'il y a trois phrases continuerait de valoir
      // consentement.
      const consentPendantLeReste =
        phaseCarte === "reste" &&
        definitif &&
        lireAcquiescement(texte).verdict === "lance";
      if (!consentPendantLeReste) oublierLaLecture();
      enTrainDeDire = "";
      phaseCarte = null;
      if (arreterSurveillance) arreterSurveillance();
      arreterSurveillance = null;
      poser("ecoute");
    }

    enAttente = texte;
    dit("onEntendu", texte);
    if (!definitif) return;

    // ⚠️ ON N'ENVOIE PAS TOUT CE QU'ON ENTEND. Un « oui », un bruit, une phrase
    // adressée à quelqu'un d'autre : en conversation il n'y a plus de clic pour
    // rattraper. Le plancher est la seule barrière.
    //
    // ⚠️ MAIS LE PLANCHER FILTRAIT À L'ENVERS : il jetait « oui », « ok »,
    // « vas-y » (un et deux mots) et laissait passer « oui enfin non »
    // (trois). Dès qu'une carte PRÊTE existe — lue ou non — une forme fermée
    // d'assentiment passe. C'est le VERDICT qui refuse, jamais le plancher :
    // sinon la relecture promise plus haut n'arriverait jamais, et un « oui »
    // resterait sans réponse.
    if (!assezPourEnvoyer(enAttente) && !assentimentSurCartePrete(enAttente)) {
      enAttente = "";
      dit("onEntendu", "");
      return;
    }
    clearTimeout(minuteur);
    minuteur = setTimeout(envoyer, SILENCE_MS);
  }

  function demarrerEcoute() {
    if (ferme) return;
    prise = ecouter({
      onTexte: entendu,
      onFin: () => {
        if (ferme) return;
        // ⚠️ Chrome coupe tout seul. Sans relance, le mode reste OUVERT à
        // l'écran et devient SOURD — l'état le plus trompeur possible.
        relances += 1;
        if (relances > RELANCES_MAX) {
          fermer(
            "L'écoute s'est arrêtée plusieurs fois de suite. J'ai fermé la conversation — reprenez à l'écrit.",
          );
          return;
        }
        // Un redémarrage immédiat lève `InvalidStateError` sur Chrome.
        setTimeout(demarrerEcoute, 250);
      },
      onErreur: (raison) => {
        if (raison === "refus") {
          fermer(
            "Le micro est refusé pour ce site. Autorisez-le dans les réglages du navigateur.",
          );
        }
        // Les autres pannes passent par `onFin`, qui relance.
      },
    });
  }

  if (!voixDisponible()) {
    // Il peut écouter mais pas répondre : on le dit et on continue — la moitié
    // du service vaut mieux que rien, à condition de ne pas le cacher.
    dit(
      "onAvis",
      "Votre navigateur ne sait pas lire à voix haute : je vous écoute, mais mes réponses restent à l'écran.",
    );
  }
  poser("ecoute");
  demarrerEcoute();

  // ── LE MICRO NETTOYÉ, EN ARRIÈRE-PLAN ──────────────────────────────────
  // On n'attend PAS son ouverture pour commencer à écouter : elle demande une
  // autorisation et peut prendre une seconde. La conversation démarre tout de
  // suite ; l'annulation d'écho arrive quand elle arrive.
  ouvrirMicroPropre()
    .then((m) => {
      if (ferme) {
        if (m) m.fermer();
        return;
      }
      micro = m;
      if (!m) {
        // Sans flux propre, la coupure au milieu n'est pas fiable : on ne la
        // promet pas. Demi-duplex d'emblée, et on le dit.
        demiDuplex = true;
        dit(
          "onAvis",
          "Je n'ai pas pu ouvrir le micro en mode annulation d'écho : je n'écouterai pas pendant que je réponds.",
        );
        return;
      }
      // ⚠️ On lit ce qui a été RÉELLEMENT appliqué, pas ce qu'on a demandé.
      if (!echoAnnule(m.reglages)) {
        demiDuplex = true;
        dit(
          "onAvis",
          "Votre navigateur n'annule pas l'écho sur ce micro : je n'écouterai pas pendant que je réponds.",
        );
      }
    })
    .catch(() => {
      demiDuplex = true;
    });

  return { fermer: () => fermer(null), enCours: () => !ferme };
}

export const _internal = {
  ASSEZ_DE_MOTS,
  SILENCE_MS,
  RELANCES_MAX,
  ECHOS_AVANT_ALERTE,
  ATTENTE_APERCU_MS,
  PAS_DE_SCRUTATION_MS,
  REMANENCE_ECHO_MS,
};
